import { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Sparkle, Check } from "@phosphor-icons/react";

// PUBLIC page — DO NOT use the authenticated `/lib/api` axios instance.
const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Forms theming — reuses the SAME theme object shape as Landing Pages.
// null theme → renders EXACTLY as before (pixel-unchanged, same guarantee
// as the LP Theming Layer).
const DEFAULT_PAGE_BG = "#0A0A0B";
const DEFAULT_FORM_BG = "#121418";
const DEFAULT_INPUT_BG = "#0A0A0B";
const DEFAULT_TEXT = "#F8FAFC";
const DEFAULT_HAIRLINE = "rgba(255,255,255,0.1)";
const DEFAULT_ACCENT_TEXT = "#0A0A0B";
const DEFAULT_BUTTON_BG = "#FFFFFF";

const FONT_CSS = {
  "system": "'Urbanist', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  "modern-sans": "'Inter', -apple-system, sans-serif",
  "editorial": "'Cormorant Garamond', Georgia, serif",
  "friendly-humanist": "'Nunito', 'Merriweather', sans-serif",
  "clean-swiss": "'DM Sans', 'Inter', sans-serif",
};

const RADIUS_CSS = { none: "0px", small: "4px", medium: "8px", large: "16px" };
const BUTTON_STYLE_CSS = {
  rounded: { borderRadius: "8px" },
  square:  { borderRadius: "0px" },
  pill:    { borderRadius: "999px" },
};

function resolveThemeVars(theme) {
  // Null theme → return null so we DON'T inject any style overrides.
  if (!theme) return null;
  const bg = theme.background_color || null;
  const isTransparent = bg && bg.toLowerCase() === "transparent";
  const pageBg = isTransparent ? "transparent" : (bg || DEFAULT_PAGE_BG);
  const formBg = isTransparent ? "transparent" : (bg || DEFAULT_FORM_BG);
  const inputBg = isTransparent ? "rgba(255,255,255,0.03)" : DEFAULT_INPUT_BG;
  const accent = theme.primary_color || DEFAULT_BUTTON_BG;
  const family = FONT_CSS[theme.font_choice] || null;
  const radius = RADIUS_CSS[theme.border_radius] || null;
  const btnRadius = BUTTON_STYLE_CSS[theme.button_style]?.borderRadius || null;
  return { pageBg, formBg, inputBg, accent, family, radius, btnRadius };
}

export default function PublicForm() {
  const { slug } = useParams();
  const [state, setState] = useState({ loading: true, form: null, error: null });
  const [values, setValues] = useState({ name: "", email: "", phone: "", company: "", hp_website: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${BACKEND}/api/public/forms/${slug}`)
      .then(r => { if (!cancelled) setState({ loading: false, form: r.data, error: null }); })
      .catch(err => {
        if (!cancelled) setState({
          loading: false, form: null,
          error: err?.response?.status === 404 ? "not_found" : "network",
        });
      });
    return () => { cancelled = true; };
  }, [slug]);

  const t = useMemo(() => resolveThemeVars(state.form?.theme), [state.form]);

  const onChange = (k, v) => setValues(prev => ({ ...prev, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    setSubmitError(null);
    if (!values.name.trim()) { setSubmitError("Please enter your name"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND}/api/public/forms/${slug}/submit`, values);
      setSubmitted(true);
    } catch (err) {
      setSubmitError(err?.response?.data?.detail || "Submission failed. Please try again.");
    } finally { setSubmitting(false); }
  };

  // Theme-driven styles. All fall back to the pre-theming look when t is null.
  const pageStyle = t
    ? { minHeight: "100vh", background: t.pageBg, color: DEFAULT_TEXT,
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 24, fontFamily: t.family || undefined }
    : null;
  const formCardStyle = t
    ? { background: t.formBg, borderRadius: t.radius || "6px",
        border: t.pageBg === "transparent" && t.formBg === "transparent"
                 ? "none" : `1px solid ${DEFAULT_HAIRLINE}` }
    : null;
  const inputStyle = t
    ? { background: t.inputBg, borderRadius: t.radius || "6px", color: DEFAULT_TEXT,
        border: `1px solid ${DEFAULT_HAIRLINE}` }
    : null;
  const buttonBaseBg = t?.accent || DEFAULT_BUTTON_BG;
  const buttonStyle = t
    ? { background: buttonBaseBg, color: DEFAULT_ACCENT_TEXT, border: "none",
        borderRadius: t.btnRadius || (t.radius === "0px" ? "0px" : "999px") }
    : null;

  return (
    <div className={t ? undefined : "min-h-screen bg-[#0A0A0B] text-[#F8FAFC] flex items-center justify-center p-6"}
         style={pageStyle || undefined}
         data-testid="public-form-page" data-themed={t ? "true" : "false"}>
      <div className="w-full max-w-md">
        {state.loading ? (
          <div className="text-center text-sm text-neutral-500" data-testid="public-form-loading">Loading…</div>
        ) : state.error === "not_found" ? (
          <div className={t ? "text-center p-10" : "text-center rounded-md border border-white/10 bg-[#121418] p-10"}
               style={formCardStyle ? { ...formCardStyle, padding: 40, textAlign: "center" } : undefined}
               data-testid="public-form-notfound">
            <div className="font-display text-2xl tracking-tight">Not found</div>
            <p className="mt-3 text-sm text-neutral-400">This form doesn't exist or is no longer accepting submissions.</p>
          </div>
        ) : state.error ? (
          <div className={t ? "text-center p-10" : "text-center rounded-md border border-white/10 bg-[#121418] p-10"}
               style={formCardStyle ? { ...formCardStyle, padding: 40, textAlign: "center" } : undefined}>
            <div className="font-display text-2xl tracking-tight">Couldn't load</div>
            <p className="mt-3 text-sm text-neutral-400">Please refresh and try again.</p>
          </div>
        ) : submitted ? (
          <div className="text-center rounded-md border border-emerald-400/25 bg-emerald-400/5 p-10" data-testid="public-form-success">
            <div className="mx-auto h-12 w-12 rounded-full border border-emerald-400/40 bg-emerald-400/10 flex items-center justify-center">
              <Check weight="bold" className="h-6 w-6 text-emerald-300" />
            </div>
            <div className="mt-4 font-display text-2xl tracking-tight">Thank you</div>
            <p className="mt-2 text-sm text-neutral-400">We received your submission and will be in touch.</p>
          </div>
        ) : (
          <form onSubmit={submit}
                className={t ? "p-8 space-y-4" : "rounded-md border border-white/10 bg-[#121418] p-8 space-y-4"}
                style={formCardStyle ? { ...formCardStyle, padding: 32 } : undefined}
                data-testid="public-form">
            <div className="text-center">
              <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.24em] text-neutral-500">
                <Sparkle weight="duotone" className="h-3 w-3" /> ApexGrey
              </div>
              <h1 className="mt-3 font-display text-2xl tracking-tight" data-testid="public-form-title">{state.form.name}</h1>
            </div>

            {["name", "email", "phone", "company"].map((k) => {
              if (k !== "name" && !state.form.fields[k].shown) return null;
              const label = k === "name" ? "Name" : k[0].toUpperCase() + k.slice(1);
              const required = k === "name" ? true : state.form.fields[k].required;
              return (
                <div key={k} className="space-y-1.5">
                  <label className="text-xs uppercase tracking-[0.15em] text-neutral-500">
                    {label} {required && "*"}
                  </label>
                  <input data-testid={`public-form-${k}`}
                    type={k === "email" ? "email" : "text"}
                    value={values[k]}
                    onChange={e => onChange(k, e.target.value)}
                    maxLength={state.form.fields[k].max_length}
                    required={required}
                    className={t ? "w-full h-10 px-3 text-sm focus:outline-none" : "w-full h-10 rounded-md bg-[#0A0A0B] border border-white/10 px-3 text-sm text-[#F8FAFC] focus:outline-none focus:border-white/30"}
                    style={inputStyle || undefined} />
                </div>
              );
            })}

            <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", height: 0, width: 0, overflow: "hidden" }}>
              <label>Website (leave empty)</label>
              <input data-testid="public-form-honeypot" tabIndex={-1} autoComplete="off"
                value={values.hp_website} onChange={e => onChange("hp_website", e.target.value)} />
            </div>

            {submitError && (
              <div className="rounded-md border border-red-400/30 bg-red-400/5 px-3 py-2 text-xs text-red-200" data-testid="public-form-error">
                {submitError}
              </div>
            )}

            <button type="submit" data-testid="public-form-submit" disabled={submitting}
              className={t ? "w-full px-4 py-2.5 text-sm font-medium disabled:opacity-60" : "w-full rounded-full bg-white text-[#0A0A0B] px-4 py-2.5 text-sm font-medium hover:bg-neutral-100 disabled:opacity-60"}
              style={buttonStyle || undefined}>
              {submitting ? "Submitting…" : "Submit"}
            </button>

            <p className="text-center text-[10px] uppercase tracking-[0.18em] text-neutral-600">Powered by ApexGrey</p>
          </form>
        )}
      </div>
    </div>
  );
}
